import React, { useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

export interface TourStep {
  /** Matches a `data-tour="<target>"` attribute on the element to spotlight. */
  target: string;
  title: string;
  description: string;
}

interface UGCEditorHelpTourProps {
  steps: TourStep[];
  /** Restricts target lookup to inside this root (the editor), so we don't
   *  accidentally spotlight a matching element elsewhere on the page. */
  rootRef: React.RefObject<HTMLElement>;
  onClose: () => void;
}

interface Rect { top: number; left: number; width: number; height: number; }

const PADDING = 8;       // breathing room around the highlighted element
const TOOLTIP_W = 280;
const GAP = 12;          // gap between highlight and tooltip

/**
 * Lightweight guided walkthrough: dims the screen, spotlights one toolbar
 * control at a time with a description, and offers Back / Next / Skip. No
 * external dependency — targets are found by `data-tour` attribute.
 */
export const UGCEditorHelpTour: React.FC<UGCEditorHelpTourProps> = ({ steps, rootRef, onClose }) => {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);

  const step = steps[index];
  const isFirst = index === 0;
  const isLast = index === steps.length - 1;

  const measure = useCallback(() => {
    const root = rootRef.current;
    if (!root || !step) { setRect(null); return; }
    const el = root.querySelector<HTMLElement>(`[data-tour="${step.target}"]`);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [rootRef, step]);

  useLayoutEffect(() => { measure(); }, [measure]);

  useEffect(() => {
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [measure]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); setIndex((i) => Math.min(steps.length - 1, i + 1)); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setIndex((i) => Math.max(0, i - 1)); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, steps.length]);

  const next = () => (isLast ? onClose() : setIndex((i) => i + 1));
  const back = () => setIndex((i) => Math.max(0, i - 1));

  if (!step) return null;

  // Highlight box (falls back to screen-center if target not found).
  const hl: Rect = rect ?? {
    top: window.innerHeight / 2 - 20,
    left: window.innerWidth / 2 - 20,
    width: 40,
    height: 40,
  };

  // Place the tooltip above the target if there's room, else below.
  const spaceAbove = hl.top;
  const placeBelow = spaceAbove < 160;
  const tooltipTop = placeBelow ? hl.top + hl.height + GAP : hl.top - GAP;
  let tooltipLeft = hl.left + hl.width / 2 - TOOLTIP_W / 2;
  tooltipLeft = Math.max(12, Math.min(window.innerWidth - TOOLTIP_W - 12, tooltipLeft));

  // Render through a portal to <body> so `position: fixed` is relative to the
  // viewport — not a transformed ancestor (the editor lives inside a panel that
  // uses CSS transforms, which would otherwise offset the spotlight + dim layer).
  return createPortal(
    <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label="Editor walkthrough">
      {/* Dim layer with a transparent cutout around the highlighted control */}
      <div className="absolute inset-0" onClick={onClose} style={{ background: 'transparent' }} />
      <div
        className="pointer-events-none absolute rounded-xl transition-all duration-200"
        style={{
          top: hl.top - PADDING,
          left: hl.left - PADDING,
          width: hl.width + PADDING * 2,
          height: hl.height + PADDING * 2,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.62)',
          border: '2px solid var(--color-accent-warm)',
        }}
      />

      {/* Tooltip card */}
      <div
        className="absolute w-[280px] rounded-2xl bg-white p-4 shadow-2xl"
        style={{
          top: tooltipTop,
          left: tooltipLeft,
          transform: placeBelow ? undefined : 'translateY(-100%)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-[#2f4941]">
            Step {index + 1} of {steps.length}
          </span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close walkthrough">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <h4 className="mb-1 text-sm font-bold text-gray-900">{step.title}</h4>
        <p className="mb-3 text-[12px] leading-relaxed text-gray-600">{step.description}</p>

        {/* Progress dots */}
        <div className="mb-3 flex items-center gap-1">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === index ? 'w-4 bg-[#2f4941]' : 'w-1.5 bg-gray-200'}`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between">
          <button onClick={onClose} className="text-[11px] font-medium text-gray-400 hover:text-gray-600">
            Skip
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button onClick={back} className="rounded-lg border border-gray-200 px-3 py-1.5 text-[11px] font-semibold text-gray-600 hover:bg-gray-50">
                Back
              </button>
            )}
            <button onClick={next} className="rounded-lg bg-brand-primary px-3.5 py-1.5 text-[11px] font-semibold text-white">
              {isLast ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UGCEditorHelpTour;
